import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, of, tap } from 'rxjs';

import { UserHelper } from '@helper/user.helper';
import { MenuHelper } from '@helper/menu.helper';
import { KeyService } from '@service/key.service';
import { WebsocketSimpleService } from '@service/websocket-simple.service';
import { AuthToken, LoginResponse, Verify2fa, Verify2faConfirm } from '@type/auth.type';
import { MenuItem } from '@type/menu.type';
import { User, UserGroupNameValues, UserLogin, UserPasswordReset, UserPasswordUpdate, UserRole } from '@type/user.type';
import { Module } from '@type/module.type';
import { environment } from '../environment/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly localStorageAuthTokenKey = 'authToken';

  get authToken(): AuthToken {
    const authTokenRaw = localStorage.getItem(this.localStorageAuthTokenKey);

    if (!authTokenRaw) {
      return;
    }

    let authToken: AuthToken;

    try {
      authToken = JSON.parse(authTokenRaw);
    } catch {
      return;
    }

    return authToken;
  }

  currentUser: User;
  currentUserGroups = this.currentUserGroupsTemplate;
  currentUserRole: UserRole;

  menuItems: MenuItem[] = [];

  httpWithoutInterceptors: HttpClient;

  get currentUserGroupsTemplate(): Record<UserGroupNameValues, boolean> {
    return {
      operator: false,
      keymaster: false,
      driver: false,
      human_resources: false,
    };
  }

  get currentUserRoleName(): UserRole {
    return this.currentUserRole;
  }

  hasUserAccessToModule(module: Module): boolean {
    return UserHelper.getRolesMatrix()[this.currentUserRole]?.includes(module);
  }

  hasUserAccessToSubmodule(routerLink: string): boolean {
    if (routerLink === '/kms') {
      return UserHelper.getRolesMatrix()[this.currentUserRole]?.includes(Module.kms);
    }

    const submoduleConfig = MenuHelper.getNavbarMenuStructure(this.keyService).find((item: MenuItem) => routerLink.substring(1).includes(item.routerLink));
    return submoduleConfig?.availableRoles?.includes(this.currentUserRoleName) ?? false;
  }


  constructor(
    private http: HttpClient,
    private httpBackend: HttpBackend,
    private readonly keyService: KeyService,
    private readonly websocketSimple: WebsocketSimpleService,
  ) {
    this.httpWithoutInterceptors = new HttpClient(httpBackend);
  }

  login(userLogin: UserLogin): Observable<LoginResponse> {
    return this.http.post<LoginResponse>('token', userLogin)
      .pipe(
        tap(loginResponse => {
          if (loginResponse.access) {
            this.saveAuthToken({
              access: loginResponse.access,
              refresh: loginResponse.refresh,
            });
          }
        }),
      );
  }

  refreshToken(): Observable<AuthToken> {
    return this.httpWithoutInterceptors.post<AuthToken>(`${environment.apiUrl}token/refresh`, {
      refresh: this.authToken?.refresh,
    })
      .pipe(
        tap(authToken => {
          this.saveAuthToken(authToken);
          this.websocketSimple.reconnect(this.authToken.access);
        }),
      );
  }

  verify2fa(verify2faConfirm: Verify2faConfirm): Observable<AuthToken> {
    return this.http.post<AuthToken>('auth/2fa/verify', verify2faConfirm)
      .pipe(
        tap(authToken => {
          this.saveAuthToken(authToken);
        }),
      );
  }

  resend2fa(challengeId: string): Observable<Verify2fa> {
    return this.http.post<Verify2fa>('auth/2fa/resend', { challengeId });
  }

  resetPassword(userPasswordReset: UserPasswordReset): Observable<UserPasswordReset> {
    return this.http.post<UserPasswordReset>('passwords/reset', userPasswordReset);
  }

  updatePassword(userPasswordUpdate: UserPasswordUpdate): Observable<UserPasswordUpdate> {
    return this.http.put<UserPasswordUpdate>('passwords', userPasswordUpdate);
  }

  logout(): void {
    this.saveAuthToken();
    this.currentUser = null;
    this.currentUserGroups = this.currentUserGroupsTemplate;
  }

  getCurrentUser(): Observable<User> {
    return this.http.get<User>('users/me');
  }

  setCurrentUser(): Observable<User> {
    if (!this.authToken) {
      this.logout();
      return of(null);
    }

    return this.getCurrentUser()
      .pipe(
        tap(currentUser => {
          this.currentUser = currentUser;
          this.currentUserRole = currentUser.role;
          this.setMenuItems();
          this.websocketSimple.connect(`${this.authToken.access}`);
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status !== 401) {
            this.logout();
          }
          return of(null);
        }),
      );
  }

  setCurrentUserGroups(): void {
    this.currentUserGroups = this.currentUserGroupsTemplate;

    for (const group of this.currentUser.groups) {
      this.currentUserGroups[group.name] = true;
    }
  }

  setMenuItems(): void {
    this.menuItems = MenuHelper.getMenuStructure().filter((item: MenuItem) => item.availableRoles.includes(this.currentUserRoleName));
  }

  saveAuthToken(authToken: AuthToken = null): void {
    localStorage.setItem(this.localStorageAuthTokenKey, JSON.stringify(authToken));
  }
}
